import React, { createRef, type FunctionComponent, useEffect } from 'react'
import JsBarcode from 'jsbarcode'
import { logError } from '@tomra/datadog-browser-logging'
import { Button } from '@tomra/design-system-react/button'

type Props = {
  charity: CharityType
}

export const CharityBarcode: FunctionComponent<Props> = ({ charity }) => {
  const barcodeContainer = createRef<HTMLDivElement>()

  const { barcode, organization } = charity
  const { appContext, displayName } = organization
  var state: string | null = null
  switch (appContext) {
    case 'RECYCLEREWARDS':
      state = 'TAS'
      break
    case 'RETURNANDEARN':
      state = 'NSW'
      break
    case 'CDSVICWEST':
      state = 'VIC'
      break
  }

  function generateBarcode() {
    try {
      if (!barcodeContainer.current || !charity) {
        return
      }

      const existingBarcode = barcodeContainer.current.querySelector('svg')
      if (existingBarcode) {
        barcodeContainer.current.removeChild(existingBarcode)
      }

      const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svgElement.setAttribute('id', 'barcode')
      svgElement.setAttribute('aria-label', 'Charity barcode')
      barcodeContainer.current.appendChild(svgElement)

      const barcodeSVGElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svgElement.setAttribute('width', barcodeContainer.current.clientWidth.toString())
      svgElement.setAttribute('style', 'background:#FFFFFF;')
      const barcodeHeight = 65

      JsBarcode(barcodeSVGElement, barcode, {
        format: 'CODE128',
        height: barcodeHeight,
        width: 2,
        displayValue: true,
        background: 'white'
      })

      // Append charity name to the main SVG element
      const fontSize = 20
      const padding = 10
      const containerWidth = barcodeContainer.current.clientWidth
      const containerHeight = barcodeContainer.current.clientHeight

      const topTextElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
      topTextElement.setAttribute('x', (svgElement?.clientWidth / 2).toString())
      topTextElement.setAttribute('y', (fontSize + padding).toString())
      topTextElement.setAttribute('fill', 'black')
      topTextElement.setAttribute('text-anchor', 'middle')
      topTextElement.setAttribute('font-family', 'sans-serif')
      topTextElement.setAttribute('font-size', fontSize.toString())
      topTextElement.textContent = displayName

      svgElement.appendChild(topTextElement)
      const textWidth = topTextElement.getBBox().width
      const textIsTooWide = textWidth > containerWidth

      if (textIsTooWide) {
        svgElement.removeChild(topTextElement)
        // If the text is too wide, we need to adjust the font size
        const adjustedFontSize = Math.floor((containerWidth / textWidth) * fontSize)
        topTextElement.setAttribute('font-size', adjustedFontSize.toString())
        svgElement.appendChild(topTextElement)
      }

      // Append the barcode SVG element to the main SVG element
      const centerOfContainer = containerWidth / 2
      const barcodeY = fontSize + padding * 1.5
      barcodeSVGElement.setAttribute('y', barcodeY.toString())
      svgElement.appendChild(barcodeSVGElement)
      barcodeSVGElement.setAttribute(
        'x',
        (centerOfContainer - barcodeSVGElement.getBoundingClientRect()?.width / 2).toString()
      )

      let totalHeight = containerHeight

      // Add bottom text
      if (state) {
        // Calculate bottom text position based on known barcode dimensions
        // JsBarcode with displayValue creates: bars (65px) + text space (~20px)
        const totalBarcodeHeight = barcodeHeight + fontSize
        const bottomTextY = barcodeY + totalBarcodeHeight + padding * 4

        const bottomTextElement = document.createElementNS('http://www.w3.org/2000/svg', 'text')
        bottomTextElement.setAttribute('x', (containerWidth / 2).toString())
        bottomTextElement.setAttribute('y', bottomTextY.toString())
        bottomTextElement.setAttribute('fill', 'black')
        bottomTextElement.setAttribute('text-anchor', 'middle')
        bottomTextElement.setAttribute('font-family', 'sans-serif')
        bottomTextElement.setAttribute('font-size', fontSize.toString())
        bottomTextElement.textContent = state

        svgElement.appendChild(bottomTextElement)
        const bottomTextWidth = bottomTextElement.getBBox().width
        const bottomTextIsTooWide = bottomTextWidth > containerWidth

        if (bottomTextIsTooWide) {
          svgElement.removeChild(bottomTextElement)
          // If the text is too wide, we need to adjust the font size
          const adjustedBottomFontSize = Math.floor((containerWidth / bottomTextWidth) * fontSize)
          bottomTextElement.setAttribute('font-size', adjustedBottomFontSize.toString())
          svgElement.appendChild(bottomTextElement)
        }

        // Update total height to include bottom text
        totalHeight = Math.max(totalHeight, bottomTextY + fontSize + padding)
      }

      // Adjust viewBox to fit all content
      svgElement.setAttribute('viewBox', `0 0 ${containerWidth} ${totalHeight}`)
    } catch (error: any) {
      logError(new Error('Unable to render charity barcode'), error)
    }
  }

  useEffect(() => {
    if (barcodeContainer.current) {
      generateBarcode()
    }
  }, [barcodeContainer, charity])

  function downloadBarcodeAsImage() {
    const svg = document.getElementById('barcode')

    if (!svg || !charity) {
      return
    }

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const scaleFactor = 4

    if (!ctx) {
      return
    }

    const svgData = new XMLSerializer().serializeToString(svg)
    const img = new Image()

    img.onload = () => {
      canvas.width = svg.clientWidth * scaleFactor
      canvas.height = svg.clientHeight * scaleFactor

      ctx.scale(scaleFactor, scaleFactor)

      ctx.drawImage(img, 0, 0)

      const url = canvas.toDataURL('image/png')
      download(url, `${displayName}-barcode.png`)
    }

    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgData)}`
  }

  function download(url: string, name: string) {
    const downloadLink = document.createElement('a')
    downloadLink.href = url
    downloadLink.download = name
    downloadLink.style.display = 'none'
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)

    // Cleanup
    setTimeout(() => URL.revokeObjectURL(url), 1000)
  }

  function downloadBarcodeAsVector() {
    const svg = document.getElementById('barcode')

    if (!svg || !charity) {
      return
    }

    const serializer = new XMLSerializer()
    const source = serializer.serializeToString(svg)

    const svgBlob = new Blob([source], { type: 'image/svg+xml;charset=utf-8' })
    const url = URL.createObjectURL(svgBlob)
    download(url, `${displayName}-barcode.svg`)
  }

  return (
    <>
      <h2 className="font-bold text-sm text-storm mb-xs ml-lg">Barcode</h2>
      <div className="card p-lg mb-md">
        <div ref={barcodeContainer} className="my-md flex items-center justify-center"></div>

        <div className="grid grid-cols-2 gap-md">
          <Button size="sm" className="col-span-1" onClick={downloadBarcodeAsImage}>
            Download PNG
          </Button>
          <Button size="sm" className="col-span-1" onClick={downloadBarcodeAsVector}>
            Download SVG
          </Button>
        </div>
      </div>
    </>
  )
}
